var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { ThemeProvider } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { Helmet } from 'react-helmet';
import { emptyCustomizations } from "./customization-types";
import { fetchBlobAsJson, THEME_FILE, LAYOUT_FILE, buildTheme, blobUrl, blobSubdomainUrl } from "./theme-helper";
import ErrorCard from "./error-card";
import LoadingIndicator from "./loading-indicator";
import { getEnvironmentFromUrl, getSubdomainFromUrl, getClientFromUrl, getIsInIframe } from "./general-helpers";
//===
// The context that holds the customizations
export var CustomizationsContext = React.createContext({
    isInitialized: false,
    environment: "",
    client: "",
    subdomain: "",
    isIframe: false,
    customizations: {},
    blobUrl: function () { return ""; },
    blobSubdomainUrl: function () { return ""; },
    blobRootUrl: function () { return ""; },
    fetchFileAsJson: function () { return {}; },
});
//===
// Provider to wrap the app and offer customizations and theme to all children.
export var CustomizationsProvider = function (_a) {
    var _b, _c;
    var path = _a.path, children = _a.children;
    var _d = useState(false), isInitialized = _d[0], setInitialized = _d[1];
    var _e = useState(emptyCustomizations), customizations = _e[0], setCustomizations = _e[1];
    var _f = useState(null), themeJson = _f[0], setThemeJson = _f[1];
    var _g = useState({}), theme = _g[0], setTheme = _g[1];
    var _h = useState(null), themeError = _h[0], setThemeError = _h[1];
    var client = useMemo(function () { return getClientFromUrl(); }, []);
    var subdomain = useMemo(function () { return getSubdomainFromUrl(); }, []);
    var environment = useMemo(function () { return getEnvironmentFromUrl(); }, []);
    var isIframe = useMemo(function () { return getIsInIframe(); }, []);
    //===
    // Effect to initialize the provider
    useEffect(function () {
        function initialize() {
            return __awaiter(this, void 0, void 0, function () {
                var themeJson, layoutJson, subdomainTheme;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            console.log("initializing Theme Provider: \r\n\tClient: " + client + "\r\n\tSubdomain: " + subdomain + "\r\n\tEnvironment: " + environment + "\r\n\tPath: " + path);
                            return [4 /*yield*/, fetchBlobAsJson(path, subdomain, THEME_FILE)];
                        case 1:
                            themeJson = _a.sent();
                            return [4 /*yield*/, fetchBlobAsJson(path, subdomain, LAYOUT_FILE)];
                        case 2:
                            layoutJson = _a.sent();
                            if (themeJson && !themeJson.isError) {
                                subdomainTheme = buildTheme(themeJson);
                                setTheme(subdomainTheme);
                                setThemeJson(themeJson);
                            }
                            if (layoutJson && !layoutJson.isError) {
                                setCustomizations(layoutJson);
                            }
                            if (!themeJson || !layoutJson || themeJson.isError || layoutJson.isError) {
                                console.error("Failed to load theme or layout.", themeJson === null || themeJson === void 0 ? void 0 : themeJson.message, layoutJson === null || layoutJson === void 0 ? void 0 : layoutJson.message);
                                setThemeError(layoutJson === null || layoutJson === void 0 ? void 0 : layoutJson.message);
                                //TODO: surface the error to the user
                            }
                            else {
                                setInitialized(true);
                            }
                            return [2 /*return*/];
                    }
                });
            });
        }
        initialize();
    }, [path, subdomain, environment, client]);
    //The context value
    var theContext = {
        isInitialized: isInitialized,
        environment: environment,
        client: client,
        subdomain: subdomain,
        customizations: customizations,
        isIframe: isIframe,
        blobUrl: function (filename) { return blobUrl(path, subdomain, filename); },
        blobSubdomainUrl: function (filename) { return blobSubdomainUrl(path, subdomain, filename, environment); },
        blobRootUrl: function (filename) { return blobUrl(path, "", filename); },
        fetchFileAsJson: function (filename) { return fetchBlobAsJson(path, subdomain, filename); }, //, environment),
    };
    if (!isInitialized || !customizations || !theme) {
        if (themeError) {
            return (_jsx(Grid, __assign({ container: true, direction: "column", alignItems: "center", style: { marginTop: "10%" } }, { children: _jsx(ErrorCard, { message: "The system failed to load the theme for the current client and subdomain", details: themeError }, void 0) }), void 0));
        }
        else {
            return _jsx(LoadingIndicator, {}, void 0);
        }
    }
    else {
        var fontIndex_1 = 0;
        var externalLinkIndex_1 = 0;
        return (_jsxs(_Fragment, { children: [_jsxs(Helmet, { children: [(_b = themeJson === null || themeJson === void 0 ? void 0 : themeJson.fonts) === null || _b === void 0 ? void 0 : _b.map(function (font) { return _jsx("style", __assign({ type: "text/css" }, { children: "@import url(" + font + ");" }), fontIndex_1++); }), (_c = themeJson === null || themeJson === void 0 ? void 0 : themeJson.externalLinks) === null || _c === void 0 ? void 0 : _c.map(function (theLink) { return _jsx("link", { rel: theLink.rel, href: theLink.href }, externalLinkIndex_1++); })] }, void 0), _jsx(CustomizationsContext.Provider, __assign({ value: theContext }, { children: _jsx(ThemeProvider, __assign({ theme: theme }, { children: children }), void 0) }), void 0)] }, void 0));
    }
};
